import React, { useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Sparkles, FileText, ChevronRight, Loader2, Zap, Eye, Pencil } from 'lucide-react';
import { generateOutline, generateContent, generateImages } from '../../services/contentService';
import { parseOutlinePages } from '../../pages/ContentHistory/historyUtils';

/* ══════════════════════════════════════════════════════════
   ContentWizardModal — 内容创作向导（两步）
   步骤 1: 配置（主题 + 页数）
   步骤 2: 大纲编辑（AI 生成 + 逐页可编辑）
   提交后创建异步任务 → 跳转历史页
   ══════════════════════════════════════════════════════════ */

const PAGE_OPTIONS = [1, 3, 5, 7, 9];

/* ── 轻量 Markdown → HTML（大纲场景：标题/加粗/列表/分隔线） ── */
const simpleMarkdown = (text) => {
    if (!text) return '';
    return text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') // escape HTML
        .replace(/^### (.+)$/gm, '<h3 class="smd-h3">$1</h3>')
        .replace(/^## (.+)$/gm, '<h2 class="smd-h2">$1</h2>')
        .replace(/^# (.+)$/gm, '<h1 class="smd-h1">$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/^[-*] (.+)$/gm, '<li class="smd-li">$1</li>')
        .replace(/^\d+\. (.+)$/gm, '<li class="smd-li smd-ol">$1</li>')
        .replace(/^---$/gm, '<hr class="smd-hr"/>')
        .replace(/\n/g, '<br/>');
};

const StepIndicator = ({ step }) => (
    <div className="flex items-center gap-3">
        {/* Step 1 */}
        <div className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${step >= 1 ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-gray-200 text-gray-500'
                }`}>1</div>
            <span className={`text-sm font-medium ${step >= 1 ? 'text-gray-900' : 'text-gray-400'}`}>配置</span>
        </div>
        {/* Line */}
        <div className={`h-0.5 w-8 rounded-full transition-all ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} />
        {/* Step 2 */}
        <div className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${step >= 2 ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-gray-200 text-gray-500'
                }`}>2</div>
            <span className={`text-sm font-medium ${step >= 2 ? 'text-gray-900' : 'text-gray-400'}`}>大纲编辑</span>
        </div>
    </div>
);

const ContentWizardModal = ({ open, onClose, hotspot, token, onComplete }) => {
    const [step, setStep] = useState(1);
    const [topic, setTopic] = useState(hotspot?.topic || hotspot?.title || '');
    const [skillName, setSkillName] = useState(hotspot?.style || 'xiaohongshu_default');
    const [pageCount, setPageCount] = useState(5);
    const [pages, setPages] = useState([]);
    const [taskId, setTaskId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [activePageIndex, setActivePageIndex] = useState(0);
    const [outlinePreview, setOutlinePreview] = useState(true);
    const autoGenerateRef = useRef(false);
    const abortRef = useRef(null);

    // Reset state when hotspot changes
    React.useEffect(() => {
        if (open && hotspot) {
            const t = hotspot?.topic || hotspot?.title || '';
            const pc = hotspot?.pageCount || 5;
            setTopic(t);
            setSkillName(hotspot?.style || 'xiaohongshu_default');
            setPageCount(pc);
            setPages([]);
            setTaskId('');
            setError('');
            setSubmitting(false);
            setActivePageIndex(0);

            // 从历史页打开 draft 任务（已有 outline + task_id）
            if (hotspot?.outline && hotspot?.task_id) {
                setTaskId(hotspot.task_id);
                // 解析已有大纲为页面
                const parsed = parseOutlinePages(hotspot.outline);
                setPages(parsed.length > 0 ? parsed : [{ type: '封面', content: '', order: 1 }]);
                setStep(2);
                setLoading(false);
            }
            // 从创作页进入时（已带 pageCount），直接进入步骤2并自动生成
            else if (hotspot?.pageCount && t) {
                setStep(2);
                setLoading(true);
                autoGenerateRef.current = true;
            } else {
                setStep(1);
                setLoading(false);
            }
        }
    }, [open, hotspot]);

    const handleGenerateOutline = useCallback(async () => {
        if (!topic.trim() || loading) return;
        // 中止上一次未完成的请求
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError('');
        try {
            const result = await generateOutline(topic, skillName, token, pageCount, { signal: controller.signal });
            if (controller.signal.aborted) return;
            const outlinePages = result?.outline?.pages || [];
            // Adjust to requested page count
            let adjusted = outlinePages.slice(0, pageCount);
            while (adjusted.length < pageCount) {
                const idx = adjusted.length + 1;
                const type = idx === 1 ? '封面' : idx === pageCount ? '总结' : '内容';
                adjusted.push({
                    type,
                    content: '',
                    order: idx,
                });
            }
            setPages(adjusted);
            setTaskId(result?.task_id || '');
            setStep(2);
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError(err.message || '大纲生成失败，请稍后重试');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [topic, pageCount, token, loading, skillName]);

    // 自动触发大纲生成（从创作页进入时，跳过步骤1）
    // 注意：reset effect 中 setState 在同一渲染周期内尚未生效，这里优先使用 hotspot 的即时配置
    React.useEffect(() => {
        if (autoGenerateRef.current && open && (hotspot?.topic || hotspot?.title || topic)) {
            autoGenerateRef.current = false;
            const effectiveTopic = hotspot?.topic || hotspot?.title || topic;
            const effectiveSkillName = hotspot?.style || skillName || 'xiaohongshu_default';
            const effectivePageCount = hotspot?.pageCount || pageCount;

            // 中止上一次未完成的请求
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            (async () => {
                try {
                    const result = await generateOutline(
                        effectiveTopic, effectiveSkillName, token, effectivePageCount,
                        { signal: controller.signal }
                    );
                    if (controller.signal.aborted) return;
                    const outlinePages = result?.outline?.pages || [];
                    let adjusted = outlinePages.slice(0, effectivePageCount);
                    while (adjusted.length < effectivePageCount) {
                        const idx = adjusted.length + 1;
                        const type = idx === 1 ? '封面' : idx === effectivePageCount ? '总结' : '内容';
                        adjusted.push({ type, content: '', order: idx });
                    }
                    setPages(adjusted);
                    setTaskId(result?.task_id || '');
                } catch (err) {
                    if (err.name === 'AbortError') return;
                    setError(err.message || '大纲生成失败，请稍后重试');
                } finally {
                    if (!controller.signal.aborted) setLoading(false);
                }
            })();
        }
    }, [open, hotspot, topic, skillName, token, pageCount]);

    const handlePageContentChange = useCallback((index, newContent) => {
        setPages(prev => prev.map((p, i) => i === index ? { ...p, content: newContent } : p));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (submitting || loading || !taskId) return;
        setSubmitting(true);

        // 拼接编辑后的大纲文本
        const outlineText = pages.map(p => `<page>${p.content}</page>`).join('\n');
        const pagePayload = pages.length > 0 ? pages : [{ type: '封面', content: topic || '', order: 1 }];

        // 后台并行执行：文案生成 & 图片生成（图片提示词来自大纲，不依赖文案）
        generateContent(taskId, topic, outlineText, token)
            .catch(err => console.warn('文案后台生成出错:', err));
        generateImages(
            taskId, pagePayload, pagePayload.length, token,
            hotspot?.referenceImageUrl || ''
        ).catch(err => console.warn('图片后台生成出错:', err));

        // 立即跳转历史页，不等待后台任务
        onComplete?.();
    }, [pages, taskId, topic, token, submitting, loading, onComplete, hotspot]);

    if (!open) return null;

    const estimatedCost = 1 + 6 * pageCount;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => { abortRef.current?.abort(); onClose(); }} />

            {/* Modal */}
            <div className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl animate-fade-in"
                style={{ maxHeight: '88vh' }}
            >
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white px-6 py-4">
                    <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-500 shadow-lg shadow-blue-200">
                            <Sparkles className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">内容创作向导</h3>
                            <StepIndicator step={step} />
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => { abortRef.current?.abort(); onClose(); }}
                        className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Error */}
                    {error && (
                        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                            {error}
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-6">
                            {/* Topic */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">主题</label>
                                <textarea
                                    value={topic}
                                    onChange={e => setTopic(e.target.value)}
                                    rows={3}
                                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100"
                                    placeholder="输入或编辑创作主题…"
                                />
                            </div>

                            {/* Page Count */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">页数</label>
                                <p className="mb-3 text-xs text-gray-400">选择小红书图片页数，每页将生成一段大纲和对应图片</p>
                                <div className="flex gap-2">
                                    {PAGE_OPTIONS.map(n => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setPageCount(n)}
                                            className={`flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-bold transition-all ${pageCount === n
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Cost estimate */}
                            <div className="flex items-center gap-2 rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
                                <Zap className="h-4 w-4 text-amber-500" />
                                <span className="text-sm text-amber-700">
                                    预估消耗 <strong>{estimatedCost}</strong> 积分（大纲 1 + 图片 {6 * pageCount}）
                                </span>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            {loading ? (
                                /* 大纲生成中 — 骨架屏 */
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-sm text-blue-600">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        <span>AI 正在生成大纲，请稍候…</span>
                                    </div>
                                    {Array.from({ length: pageCount }).map((_, i) => (
                                        <div key={i} className="animate-pulse rounded-2xl border border-gray-100 bg-gray-50 p-4">
                                            <div className="mb-3 h-5 w-16 rounded-lg bg-gray-200" />
                                            <div className="space-y-2">
                                                <div className="h-3 w-full rounded bg-gray-200" />
                                                <div className="h-3 w-4/5 rounded bg-gray-200" />
                                                <div className="h-3 w-3/5 rounded bg-gray-200" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                /* 大纲已生成 — 分栏编辑 */
                                <div>
                                    <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
                                        <FileText className="h-4 w-4" />
                                        <span>AI 已生成 {pages.length} 页大纲，点击左侧页码切换编辑</span>
                                    </div>

                                    <div className="flex gap-3" style={{ height: '360px' }}>
                                        {/* 左侧页码导航 */}
                                        <div className="flex flex-col gap-1.5 shrink-0" style={{ width: '72px' }}>
                                            {pages.map((page, idx) => {
                                                const isActive = idx === activePageIndex;
                                                const hasContent = Boolean(page.content?.trim());
                                                const label = idx === 0 ? '封面' : `第${idx + 1}页`;
                                                return (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        onClick={() => setActivePageIndex(idx)}
                                                        className={`relative flex items-center justify-center rounded-xl px-2 py-2.5 text-xs font-bold transition-all cursor-pointer ${isActive
                                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105'
                                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                                                            }`}
                                                    >
                                                        {label}
                                                        {/* 已编辑指示点 */}
                                                        {!isActive && hasContent && (
                                                            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500" />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* 右侧编辑区 */}
                                        <div className="flex-1 flex flex-col rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
                                            {/* 编辑区头部 */}
                                            <div className="flex items-center justify-between border-b border-gray-100 bg-white px-4 py-2.5">
                                                <span className={`inline-flex items-center rounded-lg px-2.5 py-0.5 text-xs font-bold ${activePageIndex === 0
                                                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white'
                                                    : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                    {activePageIndex === 0 ? '封面' : `第${activePageIndex + 1}页`}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-400">
                                                        {activePageIndex + 1} / {pages.length} 页
                                                    </span>
                                                    {!outlinePreview && (
                                                        <span className="text-[10px] text-blue-500 font-medium">编辑中…</span>
                                                    )}
                                                </div>
                                            </div>
                                            {/* 内容区：默认预览，点击进入编辑，失焦回到预览 */}
                                            <div className="flex-1 min-h-0 p-3">
                                                {outlinePreview ? (
                                                    <div
                                                        onClick={() => setOutlinePreview(false)}
                                                        className="h-full w-full rounded-xl bg-white px-4 py-3 text-sm text-gray-700 leading-relaxed overflow-y-auto smd-preview cursor-text ring-2 ring-transparent hover:ring-blue-100 transition-shadow"
                                                        dangerouslySetInnerHTML={{ __html: simpleMarkdown(pages[activePageIndex]?.content || '') || '<span class="text-gray-300">点击编辑大纲内容…</span>' }}
                                                    />
                                                ) : (
                                                    <textarea
                                                        autoFocus
                                                        value={pages[activePageIndex]?.content || ''}
                                                        onChange={e => handlePageContentChange(activePageIndex, e.target.value)}
                                                        onBlur={() => setOutlinePreview(true)}
                                                        className="h-full w-full resize-none rounded-xl border-0 bg-white px-4 py-3 text-sm text-gray-700 leading-relaxed outline-none transition-shadow placeholder:text-gray-300 ring-2 ring-blue-200"
                                                        placeholder={`${activePageIndex === 0 ? '封面' : `第${activePageIndex + 1}页`}的大纲内容…`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex shrink-0 items-center justify-between border-t border-gray-100 bg-gray-50/50 px-6 py-4">
                    <div></div>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => { abortRef.current?.abort(); onClose(); }}
                            className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
                        >
                            取消
                        </button>

                        {step === 1 && (
                            <button
                                type="button"
                                onClick={handleGenerateOutline}
                                disabled={!topic.trim() || loading}
                                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-all ${!topic.trim() || loading
                                    ? 'cursor-not-allowed bg-gray-300 shadow-none'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-500 shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5'
                                    }`}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        生成大纲中…
                                    </>
                                ) : (
                                    <>
                                        生成大纲
                                        <ChevronRight className="h-4 w-4" />
                                    </>
                                )}
                            </button>
                        )}

                        {step === 2 && (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting || loading || !taskId}
                                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-all ${submitting || loading || !taskId
                                    ? 'cursor-not-allowed bg-gray-300 shadow-none'
                                    : 'bg-gradient-to-r from-emerald-600 to-emerald-500 shadow-emerald-200 hover:shadow-xl hover:-translate-y-0.5'
                                    }`}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        提交中…
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="h-4 w-4" />
                                        提交生成
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ContentWizardModal;
